import gsap from "gsap";
import Sine from "gsap";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { setActiveScroll, setInitialPosition } from "./handleScroll";
import { handleLogo } from "./handleLogo";

gsap.registerPlugin(ScrollToPlugin, ScrollTrigger);

function onCompleteHide(to, mainContainer) {
  if (to) {
    setInitialPosition();
    const element = document.querySelector(to);
    if (element) {
      const duration = Math.abs(window.scrollY - element.offsetTop) * 0.3;
      gsap
        .to(window, {
          duration: Math.min(duration / 1000, 1.5),
          scrollTo: element,
          ease: Sine.easeOut,
        })
        .then(() => {
          handleLogo();
          mainContainer.classList.remove("disable");
        });
    }
  } else {
    mainContainer.classList.contains("disable")
      ? mainContainer.classList.remove("disable")
      : mainContainer.classList.add("disable");
  }
}

export const handleMenuClick = function(clickArgs) {
  setActiveScroll(true);
  const {
    menu,
    menuList,
    mainContainer,
    menuItems,
    to,
    logoLoad,
    logoHome,
    logoArrow,
  } = clickArgs;
  const menuClassList = menu?.classList;
  if (menuClassList?.contains("menu_open")) {
    const hideMenuItem = gsap.timeline({
      onComplete: onCompleteHide(to, mainContainer),
    });
    hideMenuItem.to(menuList, {
      duration: 0.1,
      opacity: 0,
    });
    hideMenuItem.to(menuItems, {
      duration: 0.1,
      opacity: 0,
    });
    hideMenuItem.to(logoArrow, {
      duration: 0.1,
      opacity: 0,
    });
    hideMenuItem.to(mainContainer, {
      opacity: 1,
      duration: 0.1,
      onComplete: function() {
        if (menuClassList.contains("menu_open")) {
          menuClassList.remove("menu_open");
        }
        if (menuList.classList.contains("active")) {
          menuList.classList.remove("active");
        }
        handleLogo();
      },
    });
  } else {
    const showMenuTimeline = gsap.timeline({
      onComplete: onCompleteHide(to, mainContainer),
    });
    showMenuTimeline.to(mainContainer, {
      duration: 0.1,
      opacity: 0.1,
    });
    showMenuTimeline.to(menuList, {
      duration: 0.1,
      opacity: 1,
    });
    showMenuTimeline.to(menuItems, {
      duration: 0.1,
      opacity: 1,
    });
    showMenuTimeline.to(logoHome, {
      duration: 0.1,
      opacity: 0,
    });
    showMenuTimeline.call(() => {
      if (!menuClassList.contains("menu_open")) {
        menuClassList.add("menu_open");
      }
      if (!menuList.classList.contains("active")) {
        menuList.classList.add("active");
      }
    });
  }
};

export const handleMenuItems = function(menuItems, menuClickArgs) {
  const throttledMenuClick = function(event) {
    menuClickArgs.to = event.target.dataset.to;
    handleMenuClick(menuClickArgs);
  };
  menuItems.forEach((item) =>
    item.addEventListener("click", _.throttle(throttledMenuClick, 20))
  );
};

export const handleMenuStripes = function(menuStripes, menuStripesArgs) {
  const throttleStripesClick = function(event) {
    event.preventDefault();
    handleMenuClick(menuStripesArgs);
  };
  menuStripes.addEventListener("click", _.throttle(throttleStripesClick, 20));
};

function getScaleDecrement(desktop = true) {
  return desktop ? 0.5 : 0.8;
}

export const handleMenuScrollAnimations = function(menuStripes, menuHomeLink) {
  let introContainer = document.querySelector(".intro_container");
  // If the intro container isn't set check if the page is displaying the split menu.
  //   If it is then apply the menu scroll animation.
  if (!introContainer) {
    introContainer = document.querySelector(".split-menu:nth-child(1)");
  }
  const defaultLogo = menuHomeLink.querySelector("span:first-child");
  const menuLogo = menuHomeLink.querySelector("span:last-child");
  const menuWrapper = document.querySelector('.menu');
  const menuList = document.querySelector('.menu_list');

  // Skip scroll animation when intro container doesn't exit.
  if (introContainer) {
    const bodyStyle = window.getComputedStyle(document.body);
    ScrollTrigger.saveStyles([defaultLogo])
    ScrollTrigger.matchMedia({
      "(min-width: 480px)": function() {
        // Timeline for size
        gsap
          .timeline({
            scrollTrigger: {
              trigger: introContainer,
              start: "bottom-=100 top",
              scrub: 0.3,
            },
          })
          .to(defaultLogo, { scale: getScaleDecrement(true) }, 0);
      },
      "(max-width: 479px)": function() {
        // Timeline for size
        gsap
          .timeline({
            scrollTrigger: {
              trigger: introContainer,
              start: "bottom-=100 top",
              scrub: 0.3,
            },
          })
          .to(defaultLogo, { scale: getScaleDecrement(false) }, 0);
      },
      "(min-width: 960px)": function() {
        const getInitialColor= ()=>{
          const intro = document.querySelector(".intro_container");
          const splitMenuAsFirstSection = document.querySelector(".split-menu:nth-child(1)");
          if (intro) {
            return window.Roof_defaultMenuStripesColor || bodyStyle.color || '#000';
          } else if (splitMenuAsFirstSection) {
            return window.Roof_splitMenuStripesColorDesktop || bodyStyle.color || '#000';
          } else {
            return bodyStyle.color || '#000';
          }
        }
        // Timeline for menu stripes color
        gsap
          .timeline({
            scrollTrigger: {
              trigger: introContainer,
              start: "bottom-=100 top",
              scrub: 0.3,
            },
          })
          .fromTo(menuStripes.children,  { backgroundColor: getInitialColor() }, { backgroundColor: bodyStyle.color || '#000' },0)
      },
      "(max-width: 959px)": function() {
        // Timeline for menu stripes color
        gsap
          .timeline({
            scrollTrigger: {
              trigger: introContainer,
              start: "bottom-=100 top",
              scrub: 0.3,
            },
          })
          .fromTo(menuStripes.children, { backgroundColor: window.Roof_defaultMenuStripesColor || bodyStyle.color || '#000' },{ backgroundColor: bodyStyle.color || '#000' }, 0)
      },
      all: function() {
        // Timeline for color (only applied to default logo and stripes)
        gsap
          .timeline({
            scrollTrigger: {
              trigger: introContainer,
              start: "bottom-=100 top",
              scrub: 0.3,
            },
          })
          .to(defaultLogo, { color: gsap.getProperty(menuLogo, "color") }, 0);
      },
    });

    const handleHeaderAnimation = function() {
      if (menuWrapper.classList.contains('menu_open')) {
        let tl = gsap.timeline();
        tl.to(menuLogo, {
          duration: 0.3,
          scale: gsap.getProperty(defaultLogo, "scale"),
        });
        tl.set(defaultLogo, { opacity: 1 });
        tl.to(menuLogo, { duration: 0.3, opacity: 0 });
      } else {
        let tl = gsap.timeline();
        tl.set(menuLogo, {
          duration: 0.3,
          scale: gsap.getProperty(defaultLogo, "scale"),
        });
        tl.to(menuLogo, { duration: 0.3, opacity: 1 });
        tl.set(defaultLogo, { opacity: 0 });
        tl.to(menuLogo, { duration: 0.3, scale: 1 });
      }
    }

    menuStripes.addEventListener('click', handleHeaderAnimation);

    menuList.addEventListener('click', function(event){
      if (event.target.hasAttribute('data-to')) {
        handleHeaderAnimation();
      }
    })
  }
};
